import React from 'react';
import { Features } from '../../../assets/features/Features';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';

const Methodology: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  return (
    <section id="methodology" className="methodology-section" style={{ padding: 0 }}>
      {/* Hero statement */}
      <div className="methodology-hero">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center" data-aos="fade-up">
              <div className="methodology-badge" data-aos="zoom-in" data-aos-delay="100">
                <i className="bi bi-heart-pulse"></i>
                <span>{language === 'en' ? 'My Approach' : 'A Minha Abordagem'}</span>
              </div>
              <h2 className="methodology-headline" data-aos="fade-up" data-aos-delay="200">
                {Features.methodology.painStatement[language]}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Method description */}
      <div className="methodology-content">
        <div className="container">
          <div className="row align-items-center gy-5">
            {/* Left - Description */}
            <div className="col-lg-6" data-aos="fade-right" data-aos-delay="100">
              <p className="methodology-description">
                {Features.methodology.description[language]}
              </p>

              <div className="methodology-quote" data-aos="fade-up" data-aos-delay="200">
                <div className="quote-accent"></div>
                <p>{Features.methodology.causeStatement[language]}</p>
              </div>

              <p className="methodology-text" data-aos="fade-up" data-aos-delay="300">
                {Features.methodology.approach[language]}
              </p>
            </div>

            {/* Right - Goal + Closing as visual cards */}
            <div className="col-lg-5 offset-lg-1" data-aos="fade-left" data-aos-delay="200">
              <div className="methodology-card methodology-card-goal">
                <div className="methodology-card-icon">
                  <i className="bi bi-bullseye"></i>
                </div>
                <h4>{language === 'en' ? 'The Goal' : 'O Objetivo'}</h4>
                <p>{Features.methodology.goal[language]}</p>
              </div>

              <div className="methodology-card methodology-card-closing" data-aos="fade-up" data-aos-delay="300">
                <div className="methodology-card-icon methodology-card-icon-alt">
                  <i className="bi bi-lightning-charge"></i>
                </div>
                <h4>{language === 'en' ? 'More Than Relief' : 'Mais que Alívio'}</h4>
                <p>{Features.methodology.closing[language]}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .methodology-section {
          position: relative;
          overflow: hidden;
          margin-top: 0 !important;
          padding: 0 !important;
        }

        /* Hero block */
        .methodology-hero {
          background: ${isDarkMode
            ? 'linear-gradient(135deg, #0a1628 0%, #162544 50%, #1a3a5c 100%)'
            : 'linear-gradient(135deg, #0b2545 0%, #13395e 50%, #1977cc 100%)'};
          padding: 80px 0 60px;
          position: relative;
        }

        .methodology-hero::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          width: 100%;
          height: 80px;
          background: ${isDarkMode
            ? 'linear-gradient(to top, #1a1a2e, transparent)'
            : 'linear-gradient(to top, #ffffff, transparent)'};
          pointer-events: none;
        }

        .methodology-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          border-radius: 50px;
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(10px);
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.85rem;
          font-weight: 500;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.15);
        }

        .methodology-badge i {
          font-size: 1rem;
          color: #5eb8ff;
        }

        .methodology-headline {
          font-family: var(--heading-font, 'Poppins', sans-serif);
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          line-height: 1.25;
          color: #ffffff;
          margin: 0;
          letter-spacing: -0.5px;
        }

        /* Content block */
        .methodology-content {
          padding: 60px 0 80px;
          background: ${isDarkMode ? '#1a1a2e' : '#ffffff'};
        }

        .methodology-description {
          font-size: 1.1rem;
          line-height: 1.8;
          color: ${isDarkMode ? '#c9d1d9' : '#4a5568'};
          margin-bottom: 28px;
        }

        .methodology-quote {
          position: relative;
          padding: 24px 24px 24px 28px;
          margin: 28px 0;
          border-radius: 12px;
          background: ${isDarkMode
            ? 'linear-gradient(135deg, rgba(25, 119, 204, 0.12), rgba(25, 119, 204, 0.04))'
            : 'linear-gradient(135deg, rgba(25, 119, 204, 0.08), rgba(25, 119, 204, 0.02))'};
        }

        .methodology-quote .quote-accent {
          position: absolute;
          left: 0;
          top: 12px;
          bottom: 12px;
          width: 4px;
          border-radius: 4px;
          background: linear-gradient(180deg, #1977cc, #5eb8ff);
        }

        .methodology-quote p {
          font-size: 1.2rem;
          font-weight: 600;
          font-style: italic;
          color: ${isDarkMode ? '#5eb8ff' : '#1977cc'};
          margin: 0;
          line-height: 1.6;
        }

        .methodology-text {
          font-size: 1.05rem;
          line-height: 1.8;
          color: ${isDarkMode ? '#b0b8c4' : '#5a6577'};
        }

        /* Cards */
        .methodology-card {
          padding: 32px;
          border-radius: 16px;
          margin-bottom: 20px;
          transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .methodology-card:hover {
          transform: translateY(-4px);
        }

        .methodology-card-goal {
          background: ${isDarkMode
            ? 'linear-gradient(145deg, #1e2a3a, #162236)'
            : 'linear-gradient(145deg, #f0f7ff, #e8f2fc)'};
          border: 1px solid ${isDarkMode ? 'rgba(94, 184, 255, 0.12)' : 'rgba(25, 119, 204, 0.1)'};
          box-shadow: ${isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(25, 119, 204, 0.08)'};
        }

        .methodology-card-goal:hover {
          box-shadow: ${isDarkMode
            ? '0 16px 48px rgba(0, 0, 0, 0.4)'
            : '0 16px 48px rgba(25, 119, 204, 0.14)'};
        }

        .methodology-card-closing {
          background: ${isDarkMode
            ? 'linear-gradient(145deg, #1a2436, #14202e)'
            : 'linear-gradient(145deg, #fafbff, #f3f6fc)'};
          border: 1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'};
          box-shadow: ${isDarkMode
            ? '0 4px 20px rgba(0, 0, 0, 0.2)'
            : '0 4px 20px rgba(0, 0, 0, 0.04)'};
        }

        .methodology-card-closing:hover {
          box-shadow: ${isDarkMode
            ? '0 12px 40px rgba(0, 0, 0, 0.35)'
            : '0 12px 40px rgba(0, 0, 0, 0.08)'};
        }

        .methodology-card-icon {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #1977cc, #3d9be9);
          box-shadow: 0 4px 14px rgba(25, 119, 204, 0.3);
        }

        .methodology-card-icon i {
          font-size: 1.4rem;
          color: #ffffff;
        }

        .methodology-card-icon-alt {
          background: linear-gradient(135deg, #0b2545, #1a4a7a);
          box-shadow: 0 4px 14px rgba(11, 37, 69, 0.3);
        }

        .methodology-card h4 {
          font-family: var(--heading-font, 'Poppins', sans-serif);
          font-size: 1.15rem;
          font-weight: 700;
          color: ${isDarkMode ? '#e8edf3' : '#1a2a3a'};
          margin-bottom: 10px;
        }

        .methodology-card p {
          font-size: 0.95rem;
          line-height: 1.7;
          color: ${isDarkMode ? '#9aa5b4' : '#5a6577'};
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 991.98px) {
          .methodology-hero {
            padding: 60px 0 50px;
          }
          .methodology-content {
            padding: 40px 0 60px;
          }
        }

        @media (max-width: 767.98px) {
          .methodology-hero {
            padding: 50px 0 40px;
          }
          .methodology-headline {
            font-size: 1.6rem;
          }
          .methodology-content {
            padding: 30px 0 50px;
          }
          .methodology-card {
            padding: 24px;
          }
        }
      `}</style>
    </section>
  );
};

export default Methodology;
